import React from "react";
import { Home, LogOut, Settings, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useIncidentContext } from "@/context/IncidentContext";
import { SidebarIncidentSummary } from "@/components/SidebarIncidentSummary";
import { 
  Sidebar, 
  SidebarContent, 
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  useSidebar
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";

interface AppSidebarProps {
  onCreateConnection?: () => void;
}

export default function AppSidebar({ onCreateConnection }: AppSidebarProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { setOpenMobile } = useSidebar();
  const isConnectionsPage = location.pathname === "/connections";

  // ADC-006A v2.0.0: Get incident summary for sidebar badge
  let incidentSummary;
  try {
    const ctx = useIncidentContext();
    incidentSummary = ctx.summary;
  } catch {
    // IncidentProvider may not be available on unauthenticated pages
    incidentSummary = null;
  }

  const menuItems = [
    {
      name: "Dashboard",
      icon: Home,
      path: "/dashboard",
    },
    {
      name: "Connections",
      icon: Users,
      path: "/connections",
    },
    {
      name: "Settings",
      icon: Settings,
      path: "/settings",
    },
  ];

  const handleMenuItemClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Sidebar 
      collapsible={isMobile ? "offcanvas" : "icon"}
      side="left"
      variant="sidebar"
    >
      <SidebarHeader className="py-2">
        <div className="px-3 flex items-center justify-between">
          <span className="text-lg font-semibold">mishu</span>
          <SidebarTrigger />
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        <Separator className="my-2" />
        
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item, index) => (
                <React.Fragment key={item.name}>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === item.path} tooltip={item.name}>
                      <Link to={item.path} className="flex items-center" onClick={handleMenuItemClick}>
                        <item.icon className="mr-3 h-4 w-4" />
                        <span>{item.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {/* ADC-006A: Insert Incidents nav after Dashboard */}
                  {index === 0 && incidentSummary && (
                    <SidebarMenuItem>
                      <SidebarIncidentSummary summary={incidentSummary} />
                    </SidebarMenuItem>
                  )}
                </React.Fragment>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter>
        <div className="p-3">
          <div className="rounded-lg bg-sidebar-accent p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 max-w-[calc(100%-40px)]">
                <div className="flex-shrink-0 h-9 w-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                  {user.displayName ? user.displayName[0].toUpperCase() : user.email?.[0].toUpperCase()}
                </div>
                <div className="space-y-0.5 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user.displayName || user.email}
                  </p>
                  {user.displayName && (
                    <p className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </p>
                  )}
                </div>
              </div>
              
              <Button 
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (isMobile) {
                    setOpenMobile(false);
                  }
                  logout();
                }}
                aria-label="Logout"
                className="flex-shrink-0"
              >
                <LogOut size={18} />
              </Button>
            </div>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
